/**
 * <TutorInvoicesPage> — « Mes factures » du masteur (page).
 *
 * L'ancien `TutoringInvoicesScreen` (route `tutoring-invoices`, ouvert de « Mon compte ») : les
 * factures, la plus récente d'abord, chacune avec sa date, qui l'émet et à qui, son montant, et ses
 * liens « Facture » et « Récapitulatif » (`<TutorInvoiceRow>`). Le tableau paginé au défilement
 * devient UNE carte-liste, et sa suite se demande par « Voir plus » en pied de carte — la règle des
 * listes du design system (« Activités archivées »).
 *
 * Une facture dont le PDF n'existe pas encore le fait générer : sa ligne dit « Génération... »
 * (`generatingInvoiceId`), puis le PDF s'ouvre. Un échec se dit en `<Snackbar>`, comme le toast
 * d'aujourd'hui.
 */
import React from "react"

import { DelayedSkeleton } from "../../atoms/Skeleton"
import { CardList, CardListLinkRow } from "../../molecules/CardList"
import { ClosableHeader } from "../../molecules/ClosableHeader"
import { EmptyState } from "../../molecules/EmptyState"
import { Snackbar } from "../../molecules/Snackbar"
import { TutorInvoiceRow, TutorInvoiceRowSkeleton } from "../../molecules/TutorInvoiceRow"
import { PageTemplate } from "../../templates/PageTemplate"
import { type UITutorInvoice } from "../../../ports/primary/ui/types/tutorCompte"

export type TutorInvoicesPageProps = {
    /** `null` tant que la première page n'est pas là. */
    invoices: UITutorInvoice[] | null
    onClose: () => void
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    hasMore?: boolean
    loadingMore?: boolean
    onLoadMore?: () => void
    onOpenInvoice: (invoice: UITutorInvoice) => void
    onOpenPaymentRecap: (invoice: UITutorInvoice) => void
    /** La facture dont le PDF se génère. */
    generatingInvoiceId?: number | null
    /** La génération n'est pas passée : sa phrase. */
    notice?: string | null
    onDismissNotice?: () => void
    testID?: string
}

const INVOICES_WIDTH = 720
const noop = () => {}

function TutorInvoicesSkeleton() {
    return (
        <CardList>
            <TutorInvoiceRowSkeleton />
            <TutorInvoiceRowSkeleton />
            <TutorInvoiceRowSkeleton />
        </CardList>
    )
}

export function TutorInvoicesPage(props: TutorInvoicesPageProps) {
    const {
        invoices,
        onClose,
        loading = false,
        error,
        onRetry,
        hasMore = false,
        loadingMore = false,
        onLoadMore,
        onOpenInvoice,
        onOpenPaymentRecap,
        generatingInvoiceId,
        notice,
        onDismissNotice,
        testID,
    } = props

    return (
        <PageTemplate
            testID={testID}
            header={<ClosableHeader title="Mes factures" onClose={onClose} />}
            loading={loading}
            skeleton={<TutorInvoicesSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={INVOICES_WIDTH}
        >
            {invoices != null &&
                (invoices.length === 0 ? (
                    <EmptyState iconName="file-invoice" title="Aucune facture pour le moment" />
                ) : (
                    <CardList>
                        {invoices.map((invoice) => (
                            <TutorInvoiceRow
                                key={invoice.id}
                                invoice={invoice}
                                generating={generatingInvoiceId === invoice.id}
                                onOpenInvoice={onOpenInvoice}
                                onOpenPaymentRecap={onOpenPaymentRecap}
                            />
                        ))}
                        {loadingMore ? (
                            <DelayedSkeleton>
                                <TutorInvoiceRowSkeleton />
                                <TutorInvoiceRowSkeleton divided />
                            </DelayedSkeleton>
                        ) : hasMore && onLoadMore != null ? (
                            <CardListLinkRow label="Voir plus" onPress={onLoadMore} />
                        ) : null}
                    </CardList>
                ))}
            <Snackbar
                visible={notice != null}
                message={notice ?? ""}
                tone="danger"
                onDismiss={onDismissNotice ?? noop}
            />
        </PageTemplate>
    )
}
